/**
 * Portrait game-screen map budget (pure math; GameMapHost applies it).
 *
 * - The map controls bar never starts below 65% of the window — about where
 *   the tallest maps (Japan, Minnesota, Capture the Flag) put it on a phone.
 *   Shorter maps sit well above that line.
 * - Inline tips sit on top of the controls bar. Short maps let them push the
 *   bar down; on tall maps, where they would push it past that line, the map
 *   scales down (a transform, like the chat keyboard
 *   fit) instead of re-laying-out.
 */
/** The controls bar's top edge never sits lower than this share of the window. */
export const PORTRAIT_BAR_MAX_TOP_FRACTION = 0.65

// Legacy portrait reserves (mapDimensions defaults): still the floor for the
// turn panel on short windows, where the bar line is not the binding limit.
const RESERVED_TOP = 56
const LEGACY_RESERVED_BOTTOM = 220

/**
 * reservedTop/reservedBottom for the portrait map so its height is the
 * smaller of the legacy budget and the space
 * between `mapTop` (window y of the map's top edge) and the bar line.
 */
export function portraitMapReserves(args: { windowHeight: number; mapTop: number; topOverlap?: number }): {
  reservedTop: number
  reservedBottom: number
} {
  const { windowHeight, mapTop, topOverlap = 0 } = args
  const reservedTop = Math.max(0, RESERVED_TOP - topOverlap)
  const maxHeight = windowHeight * PORTRAIT_BAR_MAX_TOP_FRACTION - mapTop
  return {
    reservedTop,
    reservedBottom: Math.max(LEGACY_RESERVED_BOTTOM, Math.round(windowHeight - reservedTop - maxHeight)),
  }
}

/**
 * Map shrink while inline tips show above the controls bar: scale the map so
 * tips + bar still start at or above the bar line, and slide the stack up by
 * the height given back. Identity when there are no tips or they fit.
 */
export function tipsFitTransform(args: {
  mapTop: number
  mapHeight: number
  tipsHeight: number
  windowHeight: number
}): { scale: number; shift: number } {
  const { mapTop, mapHeight, tipsHeight, windowHeight } = args
  if (mapHeight <= 0 || tipsHeight <= 0) return { scale: 1, shift: 0 }
  const excess = mapTop + mapHeight + tipsHeight - windowHeight * PORTRAIT_BAR_MAX_TOP_FRACTION
  if (excess <= 0) return { scale: 1, shift: 0 }
  const fitHeight = Math.max(mapHeight * 0.5, mapHeight - excess)
  return { scale: fitHeight / mapHeight, shift: Math.round(mapHeight - fitHeight) }
}
